import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid';
import * as yup from 'yup';
import dayjs from 'dayjs';
import * as fileApi from '../../lib/api/file';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { Box, IconButton } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import {
  listSalesOrderSheet,
  setSalesOrderSheetFilters,
  resetSalesOrderSheetFilters,
} from '../../modules/projectModule/sales-order-sheet';
import { SalesOrderSheetListBox } from '../../components/sales_order_sheet/SalesOrderSheetListBox';
import SalesOrderSheetList from '../../components/sales_order_sheet/SalesOrderSheetList';
import OrderSheetEditModal from '../../components/sales_order_sheet/OrderSheetEditModal';

const SalesOrderSheetListContainer = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const { files, defaultFilters, error, loading } = useSelector(
    ({ projectModule, loading }) => ({
      files: projectModule.salesOrderSheet.files,
      defaultFilters: projectModule.salesOrderSheet.defaultFilters,
      error: projectModule.salesOrderSheet.error,
      loading: loading['project/LIST_SALES_ORDER_SHEET'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listSalesOrderSheet(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (files) {
      setTableData(files);
    }
  }, [files]);

  const handleFilters = useCallback((name, value) => {
    const newFilters = { ...defaultFilters, [name]: value };

    dispatch(setSalesOrderSheetFilters(newFilters));
  }, []);

  const downloadFile = (event, file) => {
    event.ignore = true;
    const link = document.createElement('a');
    link.href = file.fileUrl;
    link.download = true;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);
  };

  const [showEdit, setShowEdit] = useState(false);
  const [selectedEdit, setSelectedEdit] = useState(null);
  const handleEdit = (param) => {
    setSelectedEdit(param);
    setShowEdit(true);
  };

  const handleEditClose = () => {
    setShowEdit(false);
    setSelectedEdit(null);
  };

  const handleSave = async (changeDate) => {
    try {
      const dateToSubmit = {
        fileUuid: selectedEdit.fileUuid,
        fileAttrVal5: dayjs(changeDate).format('YYYY-MM-DD'),
      };
      const response = await fileApi.editFile(
        selectedEdit.fileUuid,
        dateToSubmit,
      );
      if (response.data.info) {
        dispatch(listSalesOrderSheet(makeFilters(defaultFilters)));
        enqueueSnackbar('저장 되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'fileUuid',
        headerName: '파일ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'prjUuid',
        headerName: '프로젝트ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'filePartnerUuid',
        headerName: '견적서회사ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'fileRegDtAction',
        headerName: '',
        minWidth: 30,
        flex: 0.2,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="edit"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
        ),
      },
      {
        field: 'fileRegDt',
        headerName: '받을(수신)일자',
        minWidth: 120,
        flex: 1,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
      },

      {
        field: 'filePartnerNm',
        headerName: '매출처명',
        minWidth: 300,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },

      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 400,
        flex: 1,
        description: '',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
      {
        field: 'fileNm',
        headerName: '파일명',
        minWidth: 600,
        flex: 1,
        type: 'string',
        editable: false,
        disableClickEventBubbling: true,
        renderCell: (params) => (
          <div>
            {params.row?.fileNm}
            {params.row?.fileUrl && (
              <IconButton
                color="primary"
                size="small"
                sx={{ mx: 2 }}
                onClick={(e) => downloadFile(e, params.row)}
              >
                <CloudDownloadIcon />
              </IconButton>
            )}
          </div>
        ),
      },
      {
        field: 'fileCrtNm',
        headerName: '파일등록자',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
      {
        field: 'fileCrtDt',
        headerName: '파일등록일',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
    ],
    [],
  );

  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        let searchData = {
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setSalesOrderSheetFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="수주 관리/매출(받은) 발주서 현황/매출 발주서 리스트"
        icon="project"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <SalesOrderSheetListBox formik={formik} onReset={() => dispatch(resetSalesOrderSheetFilters())} />
        <SalesOrderSheetList
          apiRef={apiRef}
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
          columnVisibilityModel={{
            id: false,
            fileUuid: false,
            prjUuid: false,
            filePartnerUuid: false
          }}
        />
      </Box>
      {showEdit && (
        <OrderSheetEditModal
          currentData={selectedEdit}
          open={showEdit}
          handleClose={handleEditClose}
          onSave={handleSave}
          hiddenFields={{
            id: true,
            fileUuid: true,
            prjUuid: true,
            filePartnerUuid: true
          }}
        />
      )}
    </>
  );
};

export default SalesOrderSheetListContainer;
